<div
  class="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-surface-switch-200 bg-surface-switch-0"
>
  @if (showHeader) {
    <div class="border-b border-surface-switch-200 px-6 py-4">
      <div class="flex items-center gap-3">
        <span
          class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/20 text-lg font-bold text-primary"
        >
          2
        </span>
        <h2 class="m-0 text-xl font-semibold text-surface-switch-900">
          Select Pull Direction
        </h2>
      </div>
      <p class="mt-2 text-sm text-muted-color">
        {{ filename || "Uploaded CAD file" }}
      </p>
    </div>
  }
  <div class="flex min-h-0 flex-1">
    <div class="flex min-h-0 flex-[3_1_0%] flex-col p-4">
      <div
        class="min-h-0 flex-[3_1_0%] overflow-hidden rounded-lg border border-surface-switch-200"
      >
        @if (cadObjects.length > 0) {
          <app-cad-viewer
            [cadObjects]="cadObjects"
            [visibleCadObjectIds]="visibleCadObjectIds"
            [externallyHoveredCadObjectId]="hoveredCadObjectId"
            [pullDirections]="pullDirections"
            [selectedPullDirection]="selectedPullDirection"
            [showDraftAnalysis]="showDraftAnalysis"
            [maxDraftAngle]="maxDraftAngle"
            [enablePullDirectionPicking]="true"
            (draftAnalysisResult)="onDraftAnalysisResult($event)"
            (pullDirectionPicked)="onFaceDirectionPicked($event)"
            (hoveredCadObjectIdChange)="hoveredCadObjectId = $event"
          />
        } @else {
          <div
            class="flex h-full items-center justify-center p-6 text-center text-sm text-muted-color"
          >
            No CAD generated for this step yet.
          </div>
        }
      </div>
      <app-cad-object-tree
        class="block min-h-0 flex-[2_1_0%]"
        [cadObjects]="cadObjects"
        [primaryCadObjectId]="primaryCadObjectId"
        [cadFileId]="cadFileId"
        [hoveredCadObjectId]="hoveredCadObjectId"
        (hoveredCadObjectIdChange)="hoveredCadObjectId = $event"
        (visibleCadObjectIdsChange)="visibleCadObjectIds = $event"
      />
    </div>
    <div
      class="min-h-0 flex-[2_1_0%] overflow-y-auto border-l border-surface-switch-200 p-4 lg:w-[30rem]"
    >
      <div
        class="mb-4 rounded-md border-l-4 border-l-primary border border-surface-switch-200 bg-primary/5 px-4 pb-3"
      >
        <h3
          class="m-0 text-xs font-semibold tracking-wide uppercase text-muted-color"
        >
          Current Pull Direction
        </h3>
        <div class="mt-2 flex items-center gap-2">
          <label class="text-sm font-semibold text-muted-color">X: </label>
          <p-inputNumber
            [ngModel]="editX"
            (ngModelChange)="onEditX($event)"
            [minFractionDigits]="1"
            [maxFractionDigits]="6"
            [step]="0.1"
            [showButtons]="true"
            [inputStyle]="{ width: '8rem' }"
            size="small"
          />
          <label class="text-sm font-semibold text-muted-color">Y: </label>
          <p-inputNumber
            [ngModel]="editY"
            (ngModelChange)="onEditY($event)"
            [minFractionDigits]="1"
            [maxFractionDigits]="6"
            [step]="0.1"
            [showButtons]="true"
            [inputStyle]="{ width: '8rem' }"
            size="small"
          />
          <label class="text-sm font-semibold text-muted-color">Z: </label>
          <p-inputNumber
            [ngModel]="editZ"
            (ngModelChange)="onEditZ($event)"
            [minFractionDigits]="1"
            [maxFractionDigits]="6"
            [step]="0.1"
            [showButtons]="true"
            [inputStyle]="{ width: '8rem' }"
            size="small"
          />
        </div>
      </div>

      <h3 class="m-0 text-sm font-semibold text-surface-switch-900">
        Suggested Pull Directions
      </h3>
      @if (pullDirections.length > 0) {
        <div class="mt-3 grid grid-cols-1 gap-3">
          @for (dir of pullDirections; track $index) {
            <button
              type="button"
              (click)="onSelect(dir)"
              class="flex w-full items-center justify-center rounded-md border px-3 py-3 text-center text-sm font-medium transition-colors"
              [class]="
                isSelected(dir)
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-primary bg-surface-switch-0 text-primary hover:bg-primary/10'
              "
            >
              ({{ dir.x | number: "1.6-6" }}, {{ dir.y | number: "1.6-6" }},
              {{ dir.z | number: "1.6-6" }})
            </button>
          }
        </div>
      } @else {
        <p class="mt-4 text-sm text-muted-color">
          No pull directions available.
        </p>
      }
      @if (showFindPullDirection) {
        <span
          class="block"
          [pTooltip]="findPullDirectionTooltip ?? undefined"
          [tooltipDisabled]="findPullDirectionTooltip === null"
          tooltipPosition="top"
        >
          <button
            type="button"
            class="mt-3 flex w-full items-center justify-center rounded-md border border-primary bg-surface-switch-0 px-3 py-3 text-center text-sm font-medium text-primary transition-colors hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-60"
            [disabled]="findPullDirectionDisabled || findPullDirectionLoading"
            (click)="findPullDirection.emit()"
          >
            @if (findPullDirectionLoading) {
              <i class="pi pi-spin pi-spinner mr-2"></i>
            }
            Find Pull Directions
          </button>
        </span>
      }
      <div class="mt-6 border-t border-surface-switch-200"></div>
      <div class="mt-6 flex flex-col gap-4">
        <button
          type="button"
          (click)="useCameraDirection()"
          class="flex w-full items-center justify-center rounded-md border border-primary bg-surface-switch-0 px-3 py-3 text-center text-sm font-medium text-primary transition-colors hover:bg-primary/10"
        >
          Use Camera Direction as Pull Direction
        </button>
        <p class="m-0 text-xs text-muted-color">
          Tip: You can also hover a face to preview its pull direction, then
          click the face to select it.
        </p>
        <div class="flex flex-wrap items-center gap-3">
          <p-checkbox
            [(ngModel)]="showDraftAnalysis"
            [binary]="true"
            inputId="draftCheckbox"
          />
          <label for="draftCheckbox" class="text-sm">Show draft</label>
          <div class="w-6"></div>
          <label for="draftAngleInput" class="text-sm text-muted-color"
            >Maximum Draft Angle:</label
          >
          <p-inputNumber
            [(ngModel)]="maxDraftAngle"
            inputId="draftAngleInput"
            [min]="0"
            [max]="90"
            [step]="0.5"
            [showButtons]="true"
            [inputStyle]="{ width: '5rem' }"
          />
        </div>
        @if (showDraftAnalysis) {
          <div class="grid grid-cols-3 gap-3">
            <div
              class="rounded-md border border-surface-switch-200 bg-surface-switch-50 px-4 py-3"
            >
              <div
                class="text-xs font-semibold uppercase tracking-wide text-muted-color"
              >
                Draft Area:
              </div>
              <div class="mt-2 text-lg font-semibold text-surface-switch-900">
                {{ draftResult.draftArea / 1e6 | number: "1.1-4" }} m²
              </div>
            </div>
            <div
              class="rounded-md border border-surface-switch-200 bg-surface-switch-50 px-4 py-3"
            >
              <div
                class="text-xs font-semibold uppercase tracking-wide text-muted-color"
              >
                Visible Area:
              </div>
              <div class="mt-2 text-lg font-semibold text-surface-switch-900">
                {{ draftResult.visibleArea / 1e6 | number: "1.1-4" }} m²
              </div>
            </div>
            <div
              class="rounded-md border border-surface-switch-200 bg-surface-switch-50 px-4 py-3"
            >
              <div
                class="text-xs font-semibold uppercase tracking-wide text-muted-color"
              >
                Hidden Area:
              </div>
              <div class="mt-2 text-lg font-semibold text-surface-switch-900">
                {{ draftResult.hiddenArea / 1e6 | number: "1.1-4" }} m²
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</div>
